import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Button, Input, Select, Badge } from '../../components/ui/Kit';
import Modal from '../../components/ui/Modal';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import TeamScopeFilter from './TeamScopeFilter';
import {
  TOP25_CATEGORIES,
  categoryLabel,
  isPipelineParticipant,
  isPipelineLeadership,
  defaultTeamScope,
} from './pipelineUtils';
import {
  getActiveDirectoryUsers,
  resolveDisplayName,
  useUsersDirectory,
} from '../../lib/usersDirectory';

const EMPTY_FORM = { name: '', phone: '', email: '', category: 'trust_respect' };
const EMPTY_MACHO = {
  married: false,
  age25to55: false,
  children: false,
  homeowner: false,
  occupation: '',
};

const STATUS_VARIANT = {
  added: 'default',
  invited: 'warning',
  confirmed: 'success',
};

function machoFromContact(row) {
  return {
    married: Boolean(row?.macho_married),
    age25to55: Boolean(row?.macho_age_25_55),
    children: Boolean(row?.macho_children),
    homeowner: Boolean(row?.macho_homeowner),
    occupation: row?.macho_occupation || '',
  };
}

function machoScore(row) {
  let n = 0;
  if (row?.macho_married) n += 1;
  if (row?.macho_age_25_55) n += 1;
  if (row?.macho_children) n += 1;
  if (row?.macho_homeowner) n += 1;
  if (String(row?.macho_occupation || '').trim()) n += 1;
  return n;
}

function MachoDots({ score }) {
  return (
    <span className="inline-flex items-center gap-1.5" title={`${score}/5 MACHO`}>
      <span className="inline-flex items-center gap-0.5" aria-hidden>
        {Array.from({ length: 5 }).map((_, i) => (
          <span
            key={i}
            className={`h-1.5 w-1.5 rounded-full ${i < score ? 'bg-primary' : 'bg-lineGray'}`}
          />
        ))}
      </span>
      <span className="text-[11px] font-bold text-slateText tabular-nums">{score}/5</span>
    </span>
  );
}

function OwnTop25Editor({ user, addToast, embedded = false }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [counts, setCounts] = useState({});
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [machoContact, setMachoContact] = useState(null);
  const [machoForm, setMachoForm] = useState(EMPTY_MACHO);
  const [machoSaving, setMachoSaving] = useState(false);
  const [convertingId, setConvertingId] = useState(null);

  const load = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [rows, categoryCounts] = await Promise.all([
        api.top25Contacts.getAllForAssociate(user.id),
        api.top25Contacts.getCategoryCounts(user.id),
      ]);
      setContacts(rows || []);
      setCounts(categoryCounts || {});
    } catch (err) {
      console.error(err);
      addToast({ type: 'error', title: 'Could not load contacts', message: err.message || 'Try again.' });
    } finally {
      setLoading(false);
    }
  }, [user?.id, addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const total = useMemo(
    () => Object.values(counts).reduce((sum, n) => sum + Number(n || 0), 0),
    [counts],
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      addToast({ type: 'warning', title: 'Name required', message: 'Add a contact name.' });
      return;
    }
    setSaving(true);
    try {
      await api.top25Contacts.create({
        associateId: user.id,
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        category: form.category,
      });
      setForm(EMPTY_FORM);
      setFormOpen(false);
      addToast({ type: 'success', title: 'Contact added' });
      await load();
    } catch (err) {
      addToast({ type: 'error', title: 'Could not add contact', message: err.message || 'Try again.' });
    } finally {
      setSaving(false);
    }
  };

  const openMacho = (row) => {
    setMachoContact(row);
    setMachoForm(machoFromContact(row));
  };

  const saveMacho = async (e) => {
    e.preventDefault();
    if (!machoContact?.id) return;
    setMachoSaving(true);
    try {
      const updated = await api.top25Contacts.updateMacho(machoContact.id, machoForm);
      setContacts((prev) => prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));
      setMachoContact(null);
      addToast({ type: 'success', title: 'MACHO saved' });
    } catch (err) {
      addToast({ type: 'error', title: 'Could not save MACHO', message: err.message || 'Try again.' });
    } finally {
      setMachoSaving(false);
    }
  };

  const convertContact = async (row) => {
    if (!row?.id || !user?.id || !user?.agency_id) return;
    setConvertingId(row.id);
    try {
      const lead = await api.top25Contacts.convertToLead(row.id, user.agency_id, user.id);
      setContacts((prev) =>
        prev.map((c) => (c.id === row.id ? { ...c, converted_lead_id: lead.id } : c)),
      );
      addToast({ type: 'success', title: 'Lead created', message: `${row.name} is now in your CRM.` });
    } catch (err) {
      addToast({ type: 'error', title: 'Could not convert', message: err.message || 'Try again.' });
    } finally {
      setConvertingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 gap-3">
        <Spinner size="lg" className="text-primary" />
        <p className="text-slateText text-sm">Loading your Top 25…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {embedded ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-[15.5px] font-heading font-extrabold text-navy">Your own Top 25</h2>
            <p className="text-slateText text-sm mt-1">Build and manage your personal guest list.</p>
          </div>
          <div className="self-start rounded-[100px] border border-lineGray bg-white px-3.5 py-2 text-xs font-semibold text-slateText">
            {total} of 25 added
          </div>
        </div>
      ) : (
        <div className="flex justify-end">
          <div className="rounded-[100px] border border-lineGray bg-white px-3.5 py-2 text-xs font-semibold text-slateText">
            {total} of 25 added
          </div>
        </div>
      )}

      <Card className="p-6">
        <div className="flex items-center gap-6">
          <div className="relative w-[88px] h-[88px] shrink-0">
            <svg width="88" height="88" viewBox="0 0 88 88" className="-rotate-90">
              <circle cx="44" cy="44" r="38" fill="none" stroke="#E4E1D8" strokeWidth="8" />
              <circle
                cx="44"
                cy="44"
                r="38"
                fill="none"
                stroke="#00AAFF"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 38}`}
                strokeDashoffset={`${2 * Math.PI * 38 * (1 - Math.min(total / 25, 1))}`}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-lg font-heading font-extrabold text-navy">
              {total}/25
            </div>
          </div>
          <div>
            <h3 className="text-[15.5px] font-heading font-extrabold text-navy mb-1.5">Build your list across all 5 categories</h3>
            <p className="text-[12.5px] text-slateText max-w-md leading-relaxed">
              These are the people most likely to trust you enough to show up — pull from all five categories rather than one group of similar contacts.
            </p>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        {TOP25_CATEGORIES.map((cat) => {
          const count = Number(counts[cat.key] || 0);
          const pct = Math.min(100, Math.round((count / cat.target) * 100));
          return (
            <Card key={cat.key} className={`p-[17px] ${cat.key === 'money_conversations' ? 'sm:col-span-2' : ''}`}>
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="text-[12.8px] font-extrabold text-navy">{cat.label}</h3>
                <span className="text-[11px] font-bold text-primary">
                  {count}/{cat.target}{cat.key === 'trust_respect' ? '+' : ''}
                </span>
              </div>
              <p className="text-[11.2px] text-slateText leading-relaxed">{cat.description}</p>
              <div className="h-[5px] bg-lineGray rounded-sm mt-2.5 overflow-hidden">
                <div className="h-full bg-primary rounded-sm" style={{ width: `${pct}%` }} />
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10.5px] font-heading font-bold uppercase tracking-[1.3px] text-primary mb-1">Your List</p>
            <h2 className="text-[15.5px] font-heading font-extrabold text-navy">{total} Contacts</h2>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-lineGray">
                <th className="text-[10.5px] uppercase tracking-wide text-slateText font-bold pb-2.5 pr-2">Name</th>
                <th className="text-[10.5px] uppercase tracking-wide text-slateText font-bold pb-2.5 pr-2">Category</th>
                <th className="text-[10.5px] uppercase tracking-wide text-slateText font-bold pb-2.5 pr-2">Status</th>
                <th className="text-[10.5px] uppercase tracking-wide text-slateText font-bold pb-2.5 pr-2">MACHO</th>
                <th className="text-[10.5px] uppercase tracking-wide text-slateText font-bold pb-2.5">Lead</th>
              </tr>
            </thead>
            <tbody>
              {contacts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-sm text-slateText text-center">
                    No contacts yet. Add people from each category below.
                  </td>
                </tr>
              ) : (
                contacts.map((row) => (
                  <tr key={row.id} className="border-b border-[#F0EEE7]">
                    <td className="py-3 pr-2 text-[12.8px] font-bold text-navy">{row.name}</td>
                    <td className="py-3 pr-2">
                      <span className="inline-block text-[10.3px] font-bold px-2.5 py-0.5 rounded-[100px] bg-skyTint text-navy">
                        {categoryLabel(row.category)}
                      </span>
                    </td>
                    <td className="py-3 pr-2">
                      <Badge variant={STATUS_VARIANT[row.status] || 'default'}>
                        {row.status === 'added' ? 'Added' : row.status === 'invited' ? 'Invited' : 'Confirmed'}
                      </Badge>
                    </td>
                    <td className="py-3 pr-2">
                      <button type="button" onClick={() => openMacho(row)} className="hover:opacity-80">
                        <MachoDots score={machoScore(row)} />
                      </button>
                    </td>
                    <td className="py-3">
                      {row.converted_lead_id ? (
                        <Link
                          to={`/leads/${row.converted_lead_id}`}
                          className="text-[12px] font-bold text-primary hover:underline"
                        >
                          View Lead
                        </Link>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          isLoading={convertingId === row.id}
                          disabled={convertingId === row.id}
                          onClick={() => convertContact(row)}
                        >
                          Convert to Lead
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          onClick={() => setFormOpen(true)}
          className="flex items-center gap-2 py-3.5 text-[12.8px] font-bold text-primary hover:underline"
        >
          + Add a contact
        </button>
      </Card>

      <Modal isOpen={formOpen} onClose={() => setFormOpen(false)} title="Add a contact">
        <form onSubmit={submit} className="space-y-3 mt-4">
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
          <Input
            label="Phone"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          />
          <Input
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
          <Select
            label="Category"
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            options={TOP25_CATEGORIES.map((c) => ({ value: c.key, label: c.label }))}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button type="submit" isLoading={saving}>Add contact</Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={Boolean(machoContact)}
        onClose={() => setMachoContact(null)}
        title={machoContact ? `MACHO — ${machoContact.name}` : 'MACHO'}
      >
        <form onSubmit={saveMacho} className="space-y-3 mt-4">
          <p className="text-sm text-slateText">
            Mark the criteria that apply. Occupation counts toward the 5/5 score when filled in.
          </p>
          {[
            ['married', 'Married'],
            ['age25to55', 'Age 25-55'],
            ['children', 'Children'],
            ['homeowner', 'Homeowner'],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm text-navy">
              <input
                type="checkbox"
                checked={Boolean(machoForm[key])}
                onChange={(e) => setMachoForm((f) => ({ ...f, [key]: e.target.checked }))}
              />
              {label}
            </label>
          ))}
          <Input
            label="Occupation"
            value={machoForm.occupation}
            onChange={(e) => setMachoForm((f) => ({ ...f, occupation: e.target.value }))}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setMachoContact(null)}>Cancel</Button>
            <Button type="submit" isLoading={machoSaving}>Save MACHO</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function TeamTop25View({ user, addToast }) {
  const [scope, setScope] = useState(() => defaultTeamScope(user?.role));
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const { users: directoryUsers } = useUsersDirectory();

  const load = useCallback(async () => {
    if (!user?.agency_id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const myTeam = await api.users.getMyTeam();
      let memberIds;
      let members;
      if (scope === 'my_team') {
        members = myTeam || [];
        memberIds = members.map((u) => u.id);
      } else {
        members = getActiveDirectoryUsers(directoryUsers).filter((u) => u.id !== user.id);
        memberIds = members.map((u) => u.id);
      }

      if (memberIds.length === 0) {
        setGroups([]);
        return;
      }

      const contacts = await api.top25Contacts.listForAssociates(memberIds);
      const byAssociate = new Map();
      for (const m of members) {
        byAssociate.set(m.id, {
          associate: m,
          contacts: [],
          converted: 0,
        });
      }
      for (const row of contacts || []) {
        const bucket = byAssociate.get(row.associate_id);
        if (!bucket) continue;
        bucket.contacts.push(row);
        if (row.converted_lead_id) bucket.converted += 1;
      }
      setGroups(
        [...byAssociate.values()]
          .filter((g) => g.contacts.length > 0 || scope === 'my_team')
          .sort((a, b) => {
            const aName = resolveDisplayName(a.associate.id, a.associate) || '';
            const bName = resolveDisplayName(b.associate.id, b.associate) || '';
            return aName.localeCompare(bName);
          }),
      );
    } catch (err) {
      console.error(err);
      addToast({ type: 'error', title: 'Could not load team Top 25', message: err.message || 'Try again.' });
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [user?.agency_id, user?.id, scope, addToast, directoryUsers]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-[15.5px] font-heading font-extrabold text-navy">Team Top 25</h2>
          <p className="text-xs text-slateText mt-0.5">Read-only view of each member&apos;s list.</p>
        </div>
        <TeamScopeFilter value={scope} onChange={setScope} />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40 gap-3">
          <Spinner size="lg" className="text-primary" />
          <p className="text-slateText text-sm">Loading team lists…</p>
        </div>
      ) : groups.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-sm text-slateText">
            {scope === 'my_team'
              ? 'No team members yet, or none have Top 25 contacts.'
              : 'No Top 25 contacts found for other associates.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <Card key={group.associate.id} className="p-5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div>
                  <h3 className="text-sm font-extrabold text-navy">{resolveDisplayName(group.associate.id, group.associate) || 'Associate'}</h3>
                  <p className="text-[11px] text-slateText">
                    {group.contacts.length} contacts · {group.converted} converted
                  </p>
                </div>
              </div>
              {group.contacts.length === 0 ? (
                <p className="text-sm text-slateText">No contacts yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-lineGray">
                        <th className="text-[10.5px] uppercase tracking-wide text-slateText font-bold pb-2 pr-2">Name</th>
                        <th className="text-[10.5px] uppercase tracking-wide text-slateText font-bold pb-2 pr-2">Category</th>
                        <th className="text-[10.5px] uppercase tracking-wide text-slateText font-bold pb-2 pr-2">Status</th>
                        <th className="text-[10.5px] uppercase tracking-wide text-slateText font-bold pb-2">Lead</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.contacts.map((row) => (
                        <tr key={row.id} className="border-b border-[#F0EEE7] last:border-0">
                          <td className="py-2.5 pr-2 text-[12.5px] font-bold text-navy">{row.name}</td>
                          <td className="py-2.5 pr-2">
                            <span className="inline-block text-[10.3px] font-bold px-2.5 py-0.5 rounded-[100px] bg-skyTint text-navy">
                              {categoryLabel(row.category)}
                            </span>
                          </td>
                          <td className="py-2.5 pr-2">
                            <Badge variant={STATUS_VARIANT[row.status] || 'default'}>
                              {row.status === 'added' ? 'Added' : row.status === 'invited' ? 'Invited' : 'Confirmed'}
                            </Badge>
                          </td>
                          <td className="py-2.5 text-[12px] text-slateText">
                            {row.converted_lead_id ? (
                              <Link to={`/leads/${row.converted_lead_id}`} className="font-bold text-primary hover:underline">
                                View Lead
                              </Link>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function LeadershipTop25({ user, addToast }) {
  const [tab, setTab] = useState('team');

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-extrabold text-navy tracking-tight">
            Top <span className="text-primary">25</span> List
          </h1>
          <p className="text-slateText text-sm mt-1">
            Review team lists, or build your own Top 25 as leadership.
          </p>
        </div>
        <div className="inline-flex rounded-[100px] border border-lineGray bg-white p-0.5 text-xs font-semibold">
          <button
            type="button"
            onClick={() => setTab('team')}
            className={`rounded-[100px] px-3.5 py-1.5 ${tab === 'team' ? 'bg-navy text-white' : 'text-slateText'}`}
          >
            Team view
          </button>
          <button
            type="button"
            onClick={() => setTab('own')}
            className={`rounded-[100px] px-3.5 py-1.5 ${tab === 'own' ? 'bg-navy text-white' : 'text-slateText'}`}
          >
            My list
          </button>
        </div>
      </div>

      {tab === 'team' ? (
        <TeamTop25View user={user} addToast={addToast} />
      ) : (
        <OwnTop25Editor user={user} addToast={addToast} embedded />
      )}
    </div>
  );
}

export default function Top25List() {
  const { user } = useAuth();
  const { addToast } = useToast();

  if (isPipelineParticipant(user?.role)) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-heading font-extrabold text-navy tracking-tight">
              Your <span className="text-primary">Top 25</span> List
            </h1>
            <p className="text-slateText text-sm mt-1">The private guest list that fuels every Business Launch.</p>
          </div>
        </div>
        <OwnTop25Editor user={user} addToast={addToast} />
      </div>
    );
  }

  if (isPipelineLeadership(user?.role)) {
    return <LeadershipTop25 user={user} addToast={addToast} />;
  }

  return (
    <Card className="p-8 max-w-xl">
      <h1 className="text-xl font-heading font-bold text-navy">Top 25 List</h1>
      <p className="text-sm text-slateText mt-3">This page is available to pipeline participants and leadership.</p>
    </Card>
  );
}
